<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();

const title = ref('');
const body = ref('');
const buttons = ref([]);
const uiFlags = computed(() => getters['automationTexts/getUIFlags'].value);

const addButton = () => {
  buttons.value.push({ label: '', url: '' });
};

const removeButton = idx => {
  buttons.value.splice(idx, 1);
};

const onClose = () => {
  emit('close');
};

const addAutomationText = async () => {
  try {
    await store.dispatch('automationTexts/create', {
      title: title.value,
      body: body.value,
      buttons: buttons.value.filter(b => b.label),
      active: true,
    });
    useAlert(t('AUTOMATION_TEXT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    const errorMessage =
      error.message || t('AUTOMATION_TEXT_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="t('AUTOMATION_TEXT_MGMT.ADD.TITLE')"
      :header-content="t('AUTOMATION_TEXT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addAutomationText">
      <woot-input
        v-model="title"
        class="w-full"
        :label="t('AUTOMATION_TEXT_MGMT.FORM.TITLE.LABEL')"
        :placeholder="t('AUTOMATION_TEXT_MGMT.FORM.TITLE.PLACEHOLDER')"
      />

      <div class="w-full mb-4">
        <label class="block text-sm font-medium mb-1">
          {{ t('AUTOMATION_TEXT_MGMT.FORM.BODY.LABEL') }}
        </label>
        <textarea
          v-model="body"
          class="w-full min-h-[120px] rounded-lg border border-n-weak p-3 text-sm focus:border-n-brand focus:outline-none"
          :placeholder="t('AUTOMATION_TEXT_MGMT.FORM.BODY.PLACEHOLDER')"
        />
      </div>

      <div class="w-full mb-4">
        <label class="block text-sm font-medium mb-2">
          {{ t('AUTOMATION_TEXT_MGMT.FORM.BUTTONS.LABEL') }}
        </label>
        <div
          v-for="(btn, idx) in buttons"
          :key="idx"
          class="flex gap-2 mb-2 items-center"
        >
          <input
            v-model="btn.label"
            class="flex-1 rounded-lg border border-n-weak p-2 text-sm"
            :placeholder="
              t('AUTOMATION_TEXT_MGMT.FORM.BUTTONS.LABEL_PLACEHOLDER')
            "
          />
          <input
            v-model="btn.url"
            class="flex-1 rounded-lg border border-n-weak p-2 text-sm"
            :placeholder="
              t('AUTOMATION_TEXT_MGMT.FORM.BUTTONS.URL_PLACEHOLDER')
            "
          />
          <NextButton
            icon="i-lucide-x"
            slate
            xs
            @click.prevent="removeButton(idx)"
          />
        </div>
        <NextButton
          :label="t('AUTOMATION_TEXT_MGMT.FORM.BUTTONS.ADD')"
          faded
          slate
          xs
          @click.prevent="addButton"
        />
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="t('AUTOMATION_TEXT_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="t('AUTOMATION_TEXT_MGMT.FORM.CREATE')"
          :disabled="!title || !body || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
